import { PAWN_VISION_RADIUS, tileKeyInt } from '@dreamgame/shared';
import { signal } from '@preact/signals';

// ============================================================================
// Fog of War State - Tracks tile exploration and current visibility
// ============================================================================
// Tiles have three states:
//   - Unexplored: never been in vision range (fully hidden)
//   - Explored: previously in vision range (terrain visible, dimmed)
//   - Visible: currently in vision range (fully visible)
//
// Explored tiles persist for the session. Visibility is recomputed from
// pawn positions each time they change.
// ============================================================================

// Explored tiles persist across the session (tiles that have ever been visible)
const _exploredTiles = new Set<number>();

// Current visibility version - bumped when pawn positions change
export const fogVersion = signal(0);

// PERFORMANCE: Track pawn positions from last fog update to detect changes
const _lastPawnPositions: Array<{ x: number; y: number }> = [];
let _lastPawnCount = 0;

// Check if pawn positions changed at integer tile granularity
const havePawnPositionsChanged = ({
  pawnPositions,
}: {
  pawnPositions: ReadonlyArray<{ readonly x: number; readonly y: number }>;
}): boolean => {
  if (pawnPositions.length !== _lastPawnCount) {
    return true;
  }
  for (let i = 0; i < pawnPositions.length; i++) {
    const current = pawnPositions[i];
    const last = _lastPawnPositions[i];
    if (
      !current ||
      !last ||
      Math.floor(current.x) !== Math.floor(last.x) ||
      Math.floor(current.y) !== Math.floor(last.y)
    ) {
      return true;
    }
  }
  return false;
};

// Update the cached pawn positions array
const updateCachedPositions = ({
  pawnPositions,
}: {
  pawnPositions: ReadonlyArray<{ readonly x: number; readonly y: number }>;
}): void => {
  _lastPawnCount = pawnPositions.length;
  for (let i = 0; i < pawnPositions.length; i++) {
    const p = pawnPositions[i];
    if (!p) {
      continue;
    }
    if (!_lastPawnPositions[i]) {
      _lastPawnPositions.push({ x: 0, y: 0 });
    }
    const cached = _lastPawnPositions[i];
    if (cached) {
      cached.x = Math.floor(p.x);
      cached.y = Math.floor(p.y);
    }
  }
  _lastPawnPositions.length = pawnPositions.length;
};

// Mark all tiles within vision square of a single pawn as explored
// Uses Chebyshev distance (square area) matching the FogLayer rendering
const markPawnVisionExplored = ({ px, py }: { px: number; py: number }): void => {
  for (let dy = -PAWN_VISION_RADIUS; dy <= PAWN_VISION_RADIUS; dy++) {
    for (let dx = -PAWN_VISION_RADIUS; dx <= PAWN_VISION_RADIUS; dx++) {
      _exploredTiles.add(tileKeyInt({ x: px + dx, y: py + dy }));
    }
  }
};

// Mark tiles around current pawn positions as explored.
// Called from FogLayer each frame; only does work when pawns actually move
// to a new tile position.
export const updateExploredTiles = ({
  pawnPositions,
}: {
  pawnPositions: ReadonlyArray<{ readonly x: number; readonly y: number }>;
}): void => {
  if (!havePawnPositionsChanged({ pawnPositions })) {
    return;
  }

  updateCachedPositions({ pawnPositions });

  for (const pawn of pawnPositions) {
    if (!pawn) {
      continue;
    }
    markPawnVisionExplored({ px: Math.floor(pawn.x), py: Math.floor(pawn.y) });
  }

  fogVersion.value = fogVersion.value + 1;
};

export const isTileExplored = ({ x, y }: { x: number; y: number }): boolean => {
  return _exploredTiles.has(tileKeyInt({ x, y }));
};

export const clearFogState = (): void => {
  _exploredTiles.clear();
  _lastPawnPositions.length = 0;
  _lastPawnCount = 0;
  fogVersion.value = 0;
};
